/**
 * LineDayEditForm — "which day, new hours, new rate" for one rollup line.
 *
 * The Labor and Equipment tables show a WEEKLY line per person/asset, but the
 * money is stored per day and the Hoffman spreadsheet writes one column per
 * day. Typing a new number into the weekly cell (what this replaces) never said
 * WHICH day changed, so the server picked one — the latest — and the GC was
 * billed for the correction on a day the PM never touched.
 *
 * So: the day is chosen, not inferred. The picker lists the line's actual
 * underlying rows — date, activity code, shift, current hours and rate — and
 * Save writes absolute values to the one row selected. Nothing auto-saves.
 *
 * Layout/vocabulary follows ManualAddForm (the other explicit-save form on this
 * page): same modal shell, same Field/label treatment, same day-of-week labels.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { RollupLineDayRow, RollupLineEditRequest } from '@coffman-tm/shared';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { formatHours, formatMoneyCents } from '../lib/format';

export interface LineDayEditFormProps {
  kind: 'labor' | 'equipment';
  /** Row label for the heading — employee name or asset id. */
  lineLabel: string;
  /**
   * The on-screen line's identity key. Rows are matched to the clicked line
   * with it; for labor it can span a day row and a swing row (the detail
   * payload carries no shift), which is exactly why the picker shows the shift.
   */
  displayKey: string;
  pcoId: string;
  weekEndingDate: string;
  /** Canonical rollup id — split-week safe. Null falls back to (pco, week). */
  rollupId: string | null;
  /** Which cell the PM clicked; that field takes focus. */
  focusField: 'hours' | 'rate';
  onClose: () => void;
  /** Refetch the detail, then report what changed (for the toast). */
  onSaved: (summary: string) => Promise<void>;
}

/** "Tue 7/21" from 'YYYY-MM-DD' — same shape ManualAddForm's day picker uses. */
function dayLabel(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const dow = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
  return `${dow} ${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

export function LineDayEditForm({
  kind,
  lineLabel,
  displayKey,
  pcoId,
  weekEndingDate,
  rollupId,
  focusField,
  onClose,
  onSaved,
}: LineDayEditFormProps) {
  const { token } = useAuth();
  const [rows, setRows] = useState<RollupLineDayRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [rowId, setRowId] = useState<string>('');
  const [hours, setHours] = useState<string>('');
  const [rateDollars, setRateDollars] = useState<string>('');
  const [applyRateAllDays, setApplyRateAllDays] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Someone else moved this day while the form was open. Held separately from
  // `err` because the answer is an action (reload and look), not a retry.
  const [conflict, setConflict] = useState<{
    current: { hours: number; rate_cents: number } | null;
  } | null>(null);
  // Bumping this re-runs the load effect — the Reload button after a conflict.
  const [reloadNonce, setReloadNonce] = useState(0);

  useBodyScrollLock(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // Loaded when the form opens, not held on the page: the rows must reflect
  // the week as it is right now, including an edit made a moment ago.
  useEffect(() => {
    if (!token) return;
    let live = true;
    const load = rollupId
      ? api.rollups.lineRowsById(token, rollupId)
      : api.rollups.lineRows(token, pcoId, weekEndingDate);
    void load
      .then((res) => {
        if (!live) return;
        setRows(kind === 'labor' ? res.labor : res.equipment);
      })
      .catch((e: unknown) => {
        if (live) setLoadErr(e instanceof Error ? e.message : 'Could not load this line’s days');
      });
    return () => {
      live = false;
    };
  }, [token, rollupId, pcoId, weekEndingDate, kind, reloadNonce]);

  const lineRows = useMemo(
    () => (rows ?? []).filter((r) => r.display_key === displayKey),
    [rows, displayKey],
  );

  // One row = no ambiguity, so preselect it. More than one and the PM picks:
  // that choice IS the fix this form exists for.
  useEffect(() => {
    if (rowId !== '' || lineRows.length !== 1) return;
    const only = lineRows[0]!;
    setRowId(only.row_id);
  }, [lineRows, rowId]);

  const selected = lineRows.find((r) => r.row_id === rowId) ?? null;

  // Prefill from whatever day is selected, and re-prefill when it changes.
  useEffect(() => {
    if (!selected) return;
    setHours(String(selected.hours));
    setRateDollars((selected.rate_cents / 100).toFixed(2));
  }, [selected]);

  // The rate field takes focus once there IS a day to type against — the inputs
  // render disabled until then, and a disabled input can't hold focus.
  const hoursRef = useRef<HTMLInputElement>(null);
  const rateRef = useRef<HTMLInputElement>(null);
  const focusPlaced = useRef(false);
  useEffect(() => {
    if (rowId === '' || focusPlaced.current) return;
    const el = focusField === 'hours' ? hoursRef.current : rateRef.current;
    if (!el) return;
    el.focus();
    el.select();
    focusPlaced.current = true;
  }, [rowId, focusField]);

  // The days the SERVER will rewrite when "apply to all days" is ticked: rows
  // sharing the target's full group key. `lineRows` is the wider display-key
  // set, which for labor can union a day line and a swing line — counting that
  // would promise more days than the write touches.
  const sameLineRows = useMemo(
    () => (selected ? lineRows.filter((r) => r.line_key === selected.line_key) : []),
    [lineRows, selected],
  );
  const canSpreadRate = sameLineRows.length > 1;
  const spreadRate = applyRateAllDays && canSpreadRate;

  const hoursNum = Number(hours);
  const rateNum = Number(rateDollars);
  const rateCents = Math.round(rateNum * 100);
  const hoursChanged = !!selected && Math.abs(hoursNum - selected.hours) >= 0.0001;
  const rateChanged = !!selected && rateCents !== selected.rate_cents;
  // An unrostered row has no roster rate behind it, so its current rate is $0
  // and the form prefills $0. Saving that stamps a confirmed pair of hours × $0
  // — which outranks the roster forever, including after the person is added
  // and their rows relink. The rate has to be typed here or set in Settings
  // first; the server refuses the 0 either way.
  const unknownNeedsRate = !!selected && selected.is_unknown && !(rateCents > 0);
  const valid =
    !!selected &&
    Number.isFinite(hoursNum) &&
    hoursNum >= 0 &&
    Number.isFinite(rateNum) &&
    rateNum >= 0 &&
    !unknownNeedsRate &&
    (hoursChanged || rateChanged || spreadRate) &&
    !busy;

  const handleSave = async () => {
    if (!token || !selected || !valid) return;
    setBusy(true);
    setErr(null);
    setConflict(null);
    const body: RollupLineEditRequest = {
      row_id: selected.row_id,
      rate_cents: rateCents,
      apply_rate_to_all_days: spreadRate,
      // COMPARE-AND-SWAP. This form always posts BOTH values — the rate field
      // is prefilled and resent even for an hours-only fix — so without these
      // the second of two PMs editing the same week silently reverts the first
      // one's rate as a CONFIRMED pair, which then outranks the roster forever.
      // These say WHICH version of the day the numbers above were typed
      // against; the server refuses (409 line_changed) if it isn't that one.
      expected_hours: selected.hours,
      expected_rate_cents: selected.rate_cents,
    };
    // Only send hours when they actually moved — a rate-only correction should
    // not restate the day's hours as if the PM had retyped them.
    if (hoursChanged) body.hours = hoursNum;
    try {
      const res = rollupId
        ? kind === 'labor'
          ? await api.rollups.editLaborLineById(token, rollupId, body)
          : await api.rollups.editEquipmentLineById(token, rollupId, body)
        : kind === 'labor'
          ? await api.rollups.editLaborLine(token, pcoId, weekEndingDate, body)
          : await api.rollups.editEquipmentLine(token, pcoId, weekEndingDate, body);

      const parts: string[] = [];
      if (res.previous.hours !== res.updated.hours) {
        parts.push(`${formatHours(res.previous.hours)} → ${formatHours(res.updated.hours)} hrs`);
      }
      if (res.previous.rate_cents !== res.updated.rate_cents) {
        parts.push(
          `${formatMoneyCents(res.previous.rate_cents, { precise: true })} → ${formatMoneyCents(
            res.updated.rate_cents,
            { precise: true },
          )}`,
        );
      }
      const spread =
        res.rows_updated > 1 ? ` (rate applied to ${res.rows_updated} days)` : '';
      const summary = parts.length
        ? `${dayLabel(res.report_date)}: ${parts.join(', ')}${spread}`
        : 'No change';
      await onSaved(summary);
      onClose();
    } catch (e) {
      if (
        e instanceof ApiCallError &&
        e.kind === 'http' &&
        e.payload &&
        typeof e.payload === 'object' &&
        'error' in e.payload
      ) {
        const payload = e.payload as { error?: unknown; current?: unknown };
        const code = String(payload.error);
        if (code === 'line_changed') {
          // Nothing was written. Show what it holds now and offer the reload —
          // never re-submit automatically, which is how the lost update
          // happened in the first place.
          setConflict({ current: readCurrent(payload.current) });
          return;
        }
        setErr(errorCopy(code, kind));
      } else {
        setErr(e instanceof Error ? e.message : 'Save failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const title = `Edit hours & rate — ${lineLabel}`;

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="confirm-modal-head">
          <div className="confirm-modal-title">{title}</div>
        </div>
        <div className="confirm-modal-body">
          {loadErr && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{loadErr}</div>}
          {!loadErr && rows === null && (
            <p className="muted" style={{ fontSize: 13 }}>
              Loading this line’s days…
            </p>
          )}
          {rows !== null && lineRows.length === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              This line has no editable source rows — it may have changed since the page loaded.
              Close and reload the week.
            </p>
          )}

          {lineRows.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 4 }}>
                Which day?
              </div>
              <div
                style={{
                  border: '1px solid var(--rule)',
                  borderRadius: 4,
                  maxHeight: 190,
                  overflowY: 'auto',
                  marginBottom: 12,
                }}
              >
                {lineRows.map((r) => (
                  <label
                    key={r.row_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 9px',
                      fontSize: 13,
                      cursor: busy ? 'default' : 'pointer',
                      borderBottom: '1px solid var(--rule)',
                      background:
                        r.row_id === rowId ? 'var(--surface-2, var(--surface))' : 'transparent',
                    }}
                  >
                    <input
                      type="radio"
                      name="line-day"
                      value={r.row_id}
                      checked={r.row_id === rowId}
                      onChange={() => setRowId(r.row_id)}
                      disabled={busy}
                    />
                    <span style={{ fontWeight: 600, minWidth: 62 }}>
                      {dayLabel(r.report_date)}
                    </span>
                    <span className="mono muted" style={{ fontSize: 12, minWidth: 84 }}>
                      {r.activity_code}
                    </span>
                    {r.daypart === 'swing' && (
                      <span
                        style={{
                          fontSize: 10,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          color: 'var(--ink-3)',
                          border: '1px solid var(--rule)',
                          borderRadius: 3,
                          padding: '1px 5px',
                        }}
                      >
                        Swing
                      </span>
                    )}
                    <span className="muted" style={{ marginLeft: 'auto', fontSize: 12.5 }}>
                      {formatHours(r.hours)} hrs @{' '}
                      {formatMoneyCents(r.rate_cents, { precise: true })}
                    </span>
                  </label>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <Field label={`Hours${selected ? ` on ${dayLabel(selected.report_date)}` : ''}`}>
                  <input
                    ref={hoursRef}
                    style={{ ...inputStyle, width: 90 }}
                    type="number"
                    min="0"
                    step="0.25"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    disabled={busy || !selected}
                    aria-label="Hours for the selected day"
                  />
                </Field>
                <Field label="Rate ($)">
                  <input
                    ref={rateRef}
                    style={{ ...inputStyle, width: 110 }}
                    type="number"
                    min="0"
                    step="0.01"
                    value={rateDollars}
                    onChange={(e) => setRateDollars(e.target.value)}
                    disabled={busy || !selected}
                    aria-label="Rate for the selected day"
                  />
                </Field>
              </div>

              {unknownNeedsRate && (
                <div style={{ color: 'var(--warning)', fontSize: 12, marginTop: 8 }}>
                  {unknownRateCopy(kind)}
                </div>
              )}

              {canSpreadRate && (
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 7,
                    marginTop: 12,
                    fontSize: 12.5,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={applyRateAllDays}
                    onChange={(e) => setApplyRateAllDays(e.target.checked)}
                    disabled={busy}
                  />
                  Apply this rate to all {sameLineRows.length} days of this line
                </label>
              )}
              <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
                Hours only ever change on the day selected above. The spreadsheet bills each day
                in its own column.
              </div>
            </>
          )}

          {conflict && (
            <div
              style={{
                marginTop: 10,
                padding: '9px 10px',
                border: '1px solid var(--rule-strong, var(--rule))',
                borderLeft: '3px solid var(--warning)',
                borderRadius: 4,
                fontSize: 12.5,
                lineHeight: 1.4,
              }}
            >
              {/* The VALUES changed — never "someone changed them". Another PM
                  is the common cause, but a Settings rate edit or the Sunday-
                  premium toggle moves the effective rate too, and naming a
                  culprit would be wrong about as often as it was right. */}
              <div style={{ fontWeight: 600 }}>
                This day’s current values changed after you opened this form.
              </div>
              <div className="muted" style={{ marginTop: 3 }}>
                Nothing was saved.
                {conflict.current
                  ? ` This day now reads ${formatHours(
                      conflict.current.hours,
                    )} hrs @ ${formatMoneyCents(conflict.current.rate_cents, { precise: true })}.`
                  : ''}{' '}
                Reload to see the current values, then re-apply your change.
              </div>
              <button
                type="button"
                className="btn btn-ghost"
                style={{ marginTop: 8 }}
                onClick={() => {
                  setConflict(null);
                  setErr(null);
                  setRows(null);
                  setReloadNonce((n) => n + 1);
                }}
                disabled={busy}
              >
                Reload this line
              </button>
            </div>
          )}

          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-cta"
            onClick={handleSave}
            disabled={!valid || conflict !== null}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The server's `current` values off a 409 body, defensively. Null when the
 * payload doesn't carry them — the conflict is still real and still shown, just
 * without the "it now reads…" sentence.
 */
function readCurrent(raw: unknown): { hours: number; rate_cents: number } | null {
  if (!raw || typeof raw !== 'object') return null;
  const c = raw as { hours?: unknown; rate_cents?: unknown };
  if (typeof c.hours !== 'number' || typeof c.rate_cents !== 'number') return null;
  return { hours: c.hours, rate_cents: c.rate_cents };
}

/** Shown both before the save (inline, Save disabled) and after a server refusal. */
function unknownRateCopy(kind: 'labor' | 'equipment'): string {
  return kind === 'labor'
    ? 'This person isn’t in the roster yet. Add them in Settings first, or type their rate here.'
    : 'This asset isn’t in the roster yet. Add it in Settings first, or type its rate here.';
}

/** Server error codes the PM can actually meet, in words. */
function errorCopy(code: string, kind: 'labor' | 'equipment'): string {
  switch (code) {
    case 'rate_required_unknown_identity':
      return unknownRateCopy(kind);
    case 'rollup_locked':
      return 'This week was sent while the form was open. Reopen it to make changes.';
    case 'row_not_in_rollup':
      return 'That day is no longer part of this week. Close and reload the week.';
    case 'line_changed':
      return 'This day’s current values changed after you opened this form. Reload to see them, then re-apply your change.';
    case 'report_date_out_of_week':
      return 'That day falls outside this week — it can’t be billed here.';
    default:
      return code;
  }
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
};
